import { CardImage, Integration, PaymentOption } from '../../model'
import type { Money } from '@afterpay/types'
import {
  useApplePayEntryData,
  useCheckoutToken,
  useConsumerSelector,
  useCheckoutState,
  useCohort,
  usePaymentOptions,
  useSelectedPaymentOption,
  useOfferDetails,
  authLogout,
  closeCheckout,
  persistor,
  useAppDispatch,
  useAuthentication,
  useRedirectUrl,
  useCreateCheckoutMutation,
  CREATE_CHECKOUT_FIXED_KEY,
  useSelectedPaymentType,
} from '../../state'
import { buildSaveOfferParams, buildIssueCardParams } from './utils'
import type { AppleGatewaySaveOfferRequest, AppleGatewayIssueCardRequest, UserCohort } from '../../state/appleGateway/apis'
import { CheckoutPaymentSchedule, Cohort, OfferDetail } from '@afterpaytouch/portal-api/types'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { app } from '../../env'
import { TrackingEvent } from '../../model/monitoring'
import { useMerchantCallback } from '../../utils/merchant'
import { getCurrentUserContextFromClient, useFlag, useLDClient } from '@afterpaytouch/feature'
import { Features } from '../../model/features'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTransactionMessage } from '../../utils/transactionMessage'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { stopDatadogSession } from '@afterpaytouch/integrations/datadog'
import { useRef } from 'react'

export interface EnrichedOfferDetail extends OfferDetail {
  interestAmount?: Money
  paymentSchedules: CheckoutPaymentSchedule[]
}

export { useFinaliseApplePaylaterCheckout } from './hooks/useFinaliseApplePaylaterCheckout'

export const useAddedCardType = (): Integration | undefined => {
  const applePayEntryData = useApplePayEntryData()
  const addedCardConditions = [{ condition: applePayEntryData?.isValid, value: Integration.ApplePay }]

  const addedCardType = addedCardConditions.find(({ condition }) => condition)?.value
  return addedCardType
}

export const useCardImage = (): string => {
  const addedCardType = useAddedCardType()
  if (addedCardType === undefined) {
    return ''
  }

  return CardImage[addedCardType]
}

const getUserCohort = (cohort: Cohort | undefined): UserCohort => {
  return cohort === Cohort.CashOnly ? 'CASHAPP' : 'AFTERPAY'
}

const mapPaymentOptionToOffer = (paymentOption: PaymentOption): EnrichedOfferDetail => ({
  monthlyAmount: paymentOption.installmentAmount,
  amountDueToday: paymentOption.amountDueToday,
  totalAmount: paymentOption.totalAmount,
  numberOfInstallments: paymentOption.installmentsCount,
  apr: paymentOption.apr?.toString(),
  paymentType: paymentOption.paymentType,
  interestAmount: paymentOption.interestAmount ?? undefined,
  paymentSchedules: paymentOption.paymentSchedules ?? [],
})

const mapOfferDetailToEnriched = (offer: OfferDetail, paymentSchedules?: CheckoutPaymentSchedule[]): EnrichedOfferDetail => ({
  monthlyAmount: offer.monthlyAmount,
  amountDueToday: offer.amountDueToday,
  totalAmount: offer.totalAmount,
  numberOfInstallments: offer.numberOfInstallments,
  apr: offer.apr,
  paymentType: offer.paymentType,
  paymentSchedules: paymentSchedules ?? [],
})

export const useSaveOfferParams = (): AppleGatewaySaveOfferRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()
  const { started } = useCheckoutState()
  const cohort = useCohort()
  const paymentOptions = usePaymentOptions()
  const selectedPaymentOption = useSelectedPaymentOption()
  const offerDetails = useOfferDetails()
  const [, { data: createCheckoutData }] = useCreateCheckoutMutation({ fixedCacheKey: CREATE_CHECKOUT_FIXED_KEY })

  const userCohort = getUserCohort(cohort)
  const expires = createCheckoutData?.expires
  const selectedPaymentType = useSelectedPaymentType()
  const hasPaymentOptions = paymentOptions.length > 0

  const allOffers = hasPaymentOptions
    ? paymentOptions.map(mapPaymentOptionToOffer)
    : offerDetails.map((offer) => mapOfferDetailToEnriched(offer, started?.paymentSchedules))

  let selectedOffer: EnrichedOfferDetail | undefined

  if (hasPaymentOptions && valueIsNotNullOrUndefined(selectedPaymentOption)) {
    selectedOffer = mapPaymentOptionToOffer(selectedPaymentOption)
  } else if (!hasPaymentOptions && allOffers.length > 0) {
    if (selectedPaymentType !== null) {
      selectedOffer = allOffers.find((offer) => offer.paymentType === selectedPaymentType)
    }
    if (selectedOffer === undefined) {
      selectedOffer = allOffers[0]
    }
  }

  return buildSaveOfferParams(token, consumer, allOffers, selectedOffer, userCohort, expires)
}

export const useIssueCardParams = (): AppleGatewayIssueCardRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()

  return buildIssueCardParams(token, consumer)
}

export const useCloseApplePaylater: () => (fetchedRedirectUrl: string | undefined, queryArgs: string) => Promise<void> = () => {
  const { initial, checkout, confirmed, referrer } = useCheckoutState()
  const { endTransaction } = useMerchantCallback()
  const { authenticated, persistent } = useAuthentication()
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const redirectUrl = useRedirectUrl()
  const LDClient = useLDClient()
  const closeCount = useRef(1)
  const endTransactionMessage = useTransactionMessage()
  const isRedirectDebugEnabled = useFlag(Features.ENABLE_APPLE_PAYLATER_REDIRECT_DEBUG, false)

  const reportError = (message: string, error): void => {
    logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, {
      error: message,
      merchantName: checkout?.merchant.name,
      merchantId: checkout?.merchant.id,
    })

    const context = {
      'Merchant Data': { ...checkout?.merchant },
      'Order confirmed?': { confirmed: confirmed !== undefined, ...(confirmed ?? {}) },
      'Error details': { ...error },
    }

    capture.error(error, context)
  }

  const clearSession = async (): Promise<void> => {
    dispatch(closeCheckout())
    if (persistor === null) {
      return
    }
    await persistor.flush()
  }

  const getCallbackUrl = (): string => {
    if (redirectUrl.length === 0) {
      reportError('No baseCallbackUrl to return to', undefined)
      return referrer ?? app.BROCHURE_BASE_URL
    }
    const callbackUrl = redirectUrl + (initial?.relativeCallbackUrl ?? '')
    return callbackUrl
  }

  const trackCloseCount = (): void => {
    logEvent(TrackingEvent.CLOSE_CALLED, {
      closeCount: closeCount.current,
    })
    closeCount.current += 1
  }

  return async (fetchedRedirectUrl: string | undefined, queryArgs: string) => {
    if (isRedirectDebugEnabled) {
      logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
        step: 'close_start',
        fetchedRedirectUrl: fetchedRedirectUrl ?? 'undefined',
        queryArgs,
        redirectUrlFromState: redirectUrl,
        relativeCallbackUrl: initial?.relativeCallbackUrl ?? '',
        isWindowed: initial.isWindowed,
        isIframe: initial.isIframe,
        referrer: referrer ?? 'undefined',
      })
    }

    const logout = async (): Promise<void> => {
      if (authenticated && !persistent) {
        await dispatch(authLogout())
      }
    }

    const redirect = async (): Promise<void> => {
      trackCloseCount()

      let targetUrl: string
      let redirectSource: string

      if (fetchedRedirectUrl !== undefined && fetchedRedirectUrl.length > 0) {
        targetUrl = fetchedRedirectUrl
        redirectSource = 'fetched_url'
      } else {
        const callbackUrl = getCallbackUrl()
        targetUrl = callbackUrl + (queryArgs.length > 0 ? (callbackUrl.includes('?') ? '&' : '?') + queryArgs : '')
        redirectSource = 'fallback_url'
      }

      const stack = new Error('redirect_stack_trace').stack ?? ''

      if (isRedirectDebugEnabled) {
        logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
          step: `redirect_using_${redirectSource}`,
          redirectUrl: targetUrl,
          callbackUrl: redirectSource === 'fallback_url' ? getCallbackUrl() : undefined,
          queryArgs: redirectSource === 'fallback_url' ? queryArgs : undefined,
          stack,
        })
      }

      if (isRedirectDebugEnabled) {
        console.warn('[ApplePaylater] redirect suppressed by debug flag', { redirectUrl: targetUrl, redirectSource })
        console.trace('[ApplePaylater] redirect stack trace')
        logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
          step: 'redirect_suppressed_by_debug_flag',
          redirectUrl: targetUrl,
          redirectSource,
          stack,
        })
        return
      }

      window.location.href = targetUrl
    }

    const closePopup = async (): Promise<void> => {
      try {
        if (isRedirectDebugEnabled) {
          logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
            step: 'close_popup_end_transaction',
            endTransactionMessage,
          })
        }
        await endTransaction(endTransactionMessage)
      } catch (e) {
        reportError('No confirmation of end transaction', e)
      }
      trackCloseCount()
      await clearSession()
      if (!initial.isIframe) {
        window.close()
      }
    }

    const key = getCurrentUserContextFromClient(LDClient)?.key ?? null
    if (LDClient !== undefined && key !== null) {
      await LDClient.close()
    }

    await logout()

    stopDatadogSession()

    if (initial.isWindowed || initial.isIframe) {
      if (isRedirectDebugEnabled) {
        logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
          step: 'close_via_popup',
          isWindowed: initial.isWindowed,
          isIframe: initial.isIframe,
        })
      }
      await closePopup()
    } else {
      if (isRedirectDebugEnabled) {
        logEvent(TrackingEvent.APPLE_PAYLATER_DEBUG, {
          step: 'close_via_redirect',
        })
      }
      await redirect()
    }
  }
}
